/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef, useImperativeHandle } from 'react';

import { keyframes, css as unboundedCSS } from '@compiled/react';

import { jsx } from '@atlaskit/css';
// @ts-ignore - this is not a valid package entry point and cannot be resolved when using a modern Typescript 'moduleResolution' setting
import type { EditorAppearance } from '@atlaskit/editor-common/src/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { useContentComponentContext } from '../../hooks/useContentComponentContext';

const slideFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(1rem)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const loadingStyles = unboundedCSS({
	flexDirection: 'column',
	position: 'fixed',
	bottom: token('space.200'),
	marginLeft: token('space.negative.200'),
	marginRight: token('space.negative.200'),
	marginTop: token('space.200'),
	zIndex: 400,
	width: 'calc(var(--ak-editor--line-length, 100%) + 32px)',
	animationName: slideFadeIn,
	animationDuration: '0.2s',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'forwards',
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const baseLoadingStyles = unboundedCSS({
	flexDirection: 'column',
	position: 'fixed',
	marginLeft: token('space.negative.200'),
	marginRight: token('space.negative.200'),
	zIndex: 400,
	width: 'calc(var(--ak-editor--line-length, 100%) + 32px)',
	animationName: slideFadeIn,
	animationDuration: '0.2s',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'forwards',
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const defaultLoadingStyles = unboundedCSS({
	bottom: token('space.200'),
	// marginTop: token('space.200'),
	width: 'auto',
	left: token('space.050'),
	right: token('space.050'),
});

const pageCommentLoadingStyles = unboundedCSS({
	bottom: token('space.300'),
	left: token('space.050'),
	right: token('space.050'),
	width: 'auto',
});

const chromelessCommentLoadingStyles = unboundedCSS({
	bottom: token('space.075'),
	left: token('space.050'),
	right: token('space.050'),
	marginLeft: token('space.0'),
	marginRight: token('space.0'),
	width: 'auto',
});

interface ContainerProps {
	appearance?: EditorAppearance;
	children: React.ReactNode;
}

export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
	({ children, appearance }, ref) => {
		const internalRef = useRef<HTMLDivElement>(null);
		const { wrapperElement } = useContentComponentContext();

		useImperativeHandle(ref, () => internalRef.current as HTMLDivElement);

		const isChromelessComment = appearance === 'chromeless';
		const isPageComment = appearance === 'comment';

		// For chromeless editors, add transform to the scroll container to create
		// a new containing block for position: fixed, making fixed positioning
		// relative to the scroll container instead of the viewport
		useEffect(() => {
			if (
				!(isChromelessComment || isPageComment) ||
				!wrapperElement ||
				!expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
			) {
				return;
			}

			const originalTransform = wrapperElement.style.transform;

			// Adding transform creates a new containing block for fixed positioned descendants
			// This makes position: fixed relative to this container instead of the viewport
			wrapperElement.style.transform = 'translateZ(0)';

			return () => {
				wrapperElement.style.transform = originalTransform;
			};
		}, [isChromelessComment, isPageComment, wrapperElement]);

		if (expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)) {
			return (
				<div
					css={[
						baseLoadingStyles,
						isChromelessComment
							? chromelessCommentLoadingStyles
							: isPageComment
								? pageCommentLoadingStyles
								: defaultLoadingStyles,
					]}
					ref={internalRef}
				>
					{children}
				</div>
			);
		}

		return (
			<div css={loadingStyles} ref={internalRef}>
				{children}
			</div>
		);
	},
);
